import React, { useRef, useState } from 'react';
import {
  Input,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  FormControl,
  FormLabel,
  Checkbox,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { mundialTeamService } from '../../services/mundial/mundialTeamService';
import {
  mundialScheduleService,
  parseScheduleFromExcelBuffer,
} from '../../services/mundial/mundialScheduleService';

interface ExcelScheduleUploadProps {
  onUploadComplete: () => void;
}

export const ExcelScheduleUpload: React.FC<ExcelScheduleUploadProps> = ({
  onUploadComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const toast = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setErrors([]);

    try {
      const teams = await mundialTeamService.getAllTeams();
      if (teams.length === 0) {
        setErrors(['Primero carga el Excel de selecciones']);
        return;
      }

      const buffer = await file.arrayBuffer();
      const { rows, errors: parseErrors } = parseScheduleFromExcelBuffer(buffer);

      if (parseErrors.length > 0) {
        setErrors(parseErrors);
        return;
      }

      const result = await mundialScheduleService.importSchedule(
        rows,
        teams,
        replaceExisting
      );

      toast({
        title: 'Calendario importado',
        description: replaceExisting
          ? `Se reemplazaron ${result.deleted} partidos. Importados: ${result.imported}`
          : `Importados ${result.imported} partidos`,
        status: 'success',
        duration: 5000,
      });
      onUploadComplete();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar el archivo';
      setErrors(message.split('\n'));
      toast({
        title: 'Error',
        description: 'Revisa el formato del Excel de calendario',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="gray.600">
        Segundo Excel con dos hojas: <strong>Fase de grupos</strong> y <strong>Eliminatorias</strong>{' '}
        (esos nombres de hoja se detectan automáticamente).
      </Text>

      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Hoja</Th>
              <Th>Columnas</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Fase de grupos</Td>
              <Td>
                Fecha, Hora, Grupo, Código local, Código visitante, Estadio, Fase (= group)
              </Td>
            </Tr>
            <Tr>
              <Td>Eliminatorias</Td>
              <Td>
                Fecha, Hora, Ronda (= round_of_32, round_of_16…), Slot, Código local, Código
                visitante (origen 2A, 2B, 3ABCDF…), Estadio
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>

      <Text fontSize="xs" color="gray.500">
        En <strong>Eliminatorias</strong>, Código local/visitante son posiciones de llave (2A, 1C,
        3ABCDF), no códigos FIFA. En grupos sí son MEX, ARG, etc. Ronda: round_of_32, round_of_16,
        quarter_final, semi_final, final.
      </Text>

      <FormControl>
        <Checkbox
          isChecked={replaceExisting}
          onChange={(e) => setReplaceExisting(e.target.checked)}
        >
          Reemplazar todos los partidos del mundial existentes
        </Checkbox>
      </FormControl>

      <FormControl>
        <FormLabel>Excel de calendario y llaves (.xlsx)</FormLabel>
        <Input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFile}
          isDisabled={loading}
        />
      </FormControl>

      {errors.length > 0 && (
        <Alert status="error">
          <AlertIcon />
          <Box maxH="200px" overflowY="auto">
            {errors.map((err) => (
              <AlertDescription key={err}>{err}</AlertDescription>
            ))}
          </Box>
        </Alert>
      )}
    </VStack>
  );
};
